import React from 'react';
import { shape, node, bool, arrayOf, string } from 'prop-types';
import Person, { propTypes as personPropTypes } from '../Person';
import classNames from 'classnames';
import RegionalArticleFooter from './regionarticlefooter';
import CollapsiblePanel from '../CollapsiblePanel';

const FirstColumn = (props) => {
  return (
    <div
      className={classNames({
        'medium-4': props.contentDirection !== 'rows',
        columns: true,
      })}
    >
      {props.children}
    </div>
  );
};

const SecondColumn = (props) => {
  return (
    <div
      className={classNames({
        'medium-8': props.contentDirection !== 'rows',
        columns: true,
      })}
    >
      {props.children}
    </div>
  );
};

const ArticleFooter = (props) => {
  const {
    ArticleFooter,
    Translations,
    FooterHasCollapsibleRoleGroups,
    isRegional,
    RegionalName,
    Site
  } = props;

  if (!ArticleFooter) return null;

  const cssClasses = classNames({
    'c-articlefooter': true,
    'c-articlefooter--regional-below': isRegional,
  });

  const dataPrintSidebarTitle = Translations?.articlefooter || '';
  const isRhbVhb = Site === "rhb" || Site === "vhb";

  return (
    <footer className={cssClasses}>
      <div
        className="c-articlefooter__wrapper"
        data-print-sidebar-title={dataPrintSidebarTitle}
        data-print-sidebar-title-region={RegionalName ?? ''}
      >
        {ArticleFooter.Region && (
          <RegionalArticleFooter Heading={ArticleFooter.Region} />
        )}
        <ArticleFooterReviewDate
          Label={isRhbVhb ? Translations.approved : Translations.revisiondate}
          LastReview={ArticleFooter.LastReview}
          contentDirection={props.contentDirection}
        />
        <ArticleFooterPublishDate
          Label={Translations.publishdate}
          PublishDate={ArticleFooter.PublishDate}
          contentDirection={props.contentDirection}
        />
        <ArticleFooterApprovedDate
          Label={Translations.approved}
          ApprovedDate={ArticleFooter.approvedDate}
        />
        <ArticleFooterApprovedBy
          Label={Translations.approvedby}
          isRegional={isRegional}
          ApprovedBy={ArticleFooter.approvedBy || ArticleFooter.Organizations}
        />

        {ArticleFooter.RoleGroups && (
          <ArticleFooterContent
            ArticleFooter={ArticleFooter}
            FooterHasCollapsibleRoleGroups={FooterHasCollapsibleRoleGroups}
            contentDirection={props.contentDirection}
            Translations={Translations}
          />
        )}
      </div>
      {ArticleFooter.InformationText && (
        <div
          className="c-articlefooter__info-text"
          dangerouslySetInnerHTML={{
            __html: props.ArticleFooter.InformationText,
          }}
        />
      )}
    </footer>
  );
};

const ArticleFooterContent = (props) => {
  return (
    props.ArticleFooter.RoleGroups &&
    props.ArticleFooter.RoleGroups.map((item, index) => {
      return (
        <div key={index} className="c-articlefooter__content">
          <div className="row">
            <FirstColumn contentDirection={props.contentDirection}>
              <span className="c-articlefooter__key">{item.Label}: </span>
            </FirstColumn>

            {props.FooterHasCollapsibleRoleGroups ? (
              <SecondColumn contentDirection={props.contentDirection}>
                <CollapsiblePanel
                  Id="rolegroup"
                  className="c-collapsible-panel__workgroup c-collapsible-panel"
                  DefaultCollapsed={true}
                  Heading={props.Translations?.showworkgroup}
                >
                  <RoleItem key={index} {...item} />
                </CollapsiblePanel>
              </SecondColumn>
            ) : (
              <SecondColumn contentDirection={props.contentDirection}>
                <RoleItem key={index} {...item} />
              </SecondColumn>
            )}
          </div>
        </div>
      );
    })
  );
};

const RoleItem = (props) => {
  const items =
    props.Persons &&
    props.Persons.map((person, index) => {
      return <Person key={index} {...person} />;
    });
  return items;
};

const ArticleFooterReviewDate = ({ Label, LastReview, contentDirection }) => {
  if (!LastReview) return null;

  return (
    <div className="row c-articlefooter__content">
      <FirstColumn contentDirection={contentDirection}>
        <span className="c-articlefooter__key">{Label}:</span>
      </FirstColumn>
      <SecondColumn contentDirection={contentDirection}>
        <span className="c-articlefooter__value">{LastReview}</span>
      </SecondColumn>
    </div>
  );
};

const ArticleFooterPublishDate = ({ Label, PublishDate, contentDirection }) => {
  if (!PublishDate) return null;

  return (
    <div className="row c-articlefooter__content">
      <FirstColumn contentDirection={contentDirection}>
        <span className="c-articlefooter__key">{Label}:</span>
      </FirstColumn>
      <SecondColumn contentDirection={contentDirection}>
        <span className="c-articlefooter__value">{PublishDate}</span>
      </SecondColumn>
    </div>
  );
};

const ArticleFooterApprovedDate = ({ Label, ApprovedDate }) => {
  if (!ApprovedDate) return null;

  return (
    <div className="row c-articlefooter__content">
      <FirstColumn>
        <span className="c-articlefooter__key">{Label}:</span>
      </FirstColumn>
      <SecondColumn>
        <span className="c-articlefooter__value">{ApprovedDate}</span>
      </SecondColumn>
    </div>
  );
};

const ArticleFooterApprovedBy = ({ Label, ApprovedBy, isRegional }) => {
  if (!ApprovedBy) return null;
  return (
    <div className="row c-articlefooter__content">
      <FirstColumn>
        <span className="c-articlefooter__key">{Label}:</span>
      </FirstColumn>
      <SecondColumn>
        {isRegional ? (
          <span className="c-articlefooter__value">{ApprovedBy}</span>
        ) : (
          ApprovedBy.map(
            (item, i) =>
              item.name && (
                <span className="c-articlefooter__value" key={i}>
                  {item.url ? <a href={item.url}>{item.name}</a> : item.name}
                </span>
              )
          )
        )}
      </SecondColumn>
    </div>
  );
};

ArticleFooter.propTypes = {
  ArticleFooter: shape({
    Region: node,
    PublishDate: node,
    LastReview: node,
    ApprovedDate: node,
    ApprovedBy: node,
    Rolegroups: arrayOf(
      shape({
        Label: node,
        Persons: arrayOf(
          shape({
            ...personPropTypes,
          })
        ),
      })
    ),
  }),
  Translations: shape({
    revisiondate: string,
    publishdate: string,
    approveddate: string,
    approvedby: string,
  }),
  isRegional: bool,
  RegionalName: string,
  HasFooterHeading: bool,
  NationalHeading: string,
  FooterHasCollapsibleRoleGroups: bool,
  contentDirection: string,
};

export default ArticleFooter;
